import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
  InputAdornment,
  Box,
  Divider,
  Button,
} from '@mui/material';
import { HelpOutline, Clear, Add, Delete } from '@mui/icons-material';
import { CustomeTextField } from '../generic/CustomeTextField';
import { CustomEditor } from '../core/CustomEditor';
import { DocumentCard } from '../DocumentPortal/DocumentSortingDialog/DocumentCard';
import { AddDocumentDialog } from './AddDocumentDialog';
import type { IDocument } from '../Interfaces/IDocument';

interface GenericMeetingEmailFormFields<FormType> {
  form: FormType;
  onField: <K extends keyof FormType>(key: K, value: FormType[K]) => void;
  docs: IDocument[];
  onAddDoc: (docs: IDocument[]) => void;
  onRemoveDoc: (idx: number) => void;
}

export function GenericMeetingEmailFormFields<
  FormType extends {
    Title: string;
    Subject: string;
    Body: string;
    DaysBeforeDate: number | null;
    sendOnCreation: boolean;
    sendOnReschedule: boolean;
    sendOnCancelling: boolean;
    sendWithCalenderFile: boolean;
  },
>(props: GenericMeetingEmailFormFields<FormType>) {
  const { form, onField, docs, onAddDoc, onRemoveDoc } = props;
  const [addOpen, setAddOpen] = useState(false);
  const toggleFields: Array<{
    key:
      | 'sendOnCreation'
      | 'sendOnReschedule'
      | 'sendOnCancelling'
      | 'sendWithCalenderFile';
    label: string;
  }> = [
    { key: 'sendOnCreation', label: 'Termin erstellen' },
    { key: 'sendOnReschedule', label: 'Terminzeit ändern' },
    { key: 'sendOnCancelling', label: 'Termin absagen' },
    { key: 'sendWithCalenderFile', label: 'Kalenderdatei mitsenden' },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CustomeTextField
            label='Titel'
            type='string'
            attr='Title'
            object={form}
            setObject={(updated: any) => onField('Title', updated.Title as FormType['Title'])}
            size='small'
            maxLength={45}
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='subtitle1'>Senden bei:</Typography>
          <Tooltip title='Mit den Checkboxen legen Sie fest, ob die E-Mail bei Erstellung, Änderung (der Terminzeit) oder Löschung des Termins im Vorgang verschickt wird. Im Feld Versandzeitpunkt können Sie unabhängig davon festlegen ob die E-Mail X Tage vor oder nach dem Termindatum versendet wird. (Ein positiver Wert ist vor, ein negativer Wert nach dem Termin)'>
            <IconButton size='small' sx={{ ml: 1 }}>
              <HelpOutline fontSize='small' />
            </IconButton>
          </Tooltip>
        </Grid>
        {toggleFields.map(({ key, label }) => (
          <Grid item xs={6} sm={4} key={key as string}>
            <FormControlLabel
              control={
                <Switch
                  checked={form[key]}
                  onChange={(_, checked) =>
                    onField(key, checked as FormType[typeof key])
                  }
                />
              }
              label={label}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} md={6}>
              <TextField
                label='Versandzeitpunkt (± Tage vor Termin)'
                size='small'
                type='number'
                value={form.DaysBeforeDate ?? ''}
                onChange={e =>
                  onField(
                    'DaysBeforeDate',
                    isNaN(Number(e.target.value))
                      ? null
                      : (Number(e.target.value) as FormType['DaysBeforeDate'])
                  )
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => onField('DaysBeforeDate', null)}
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <CustomeTextField
            label='Betreff'
            type='string'
            attr='Subject'
            object={form}
            setObject={(updated: any) => onField('Subject', updated.Subject as FormType['Subject'])}
            size='small'
            maxLength={45}
          />
        </Grid>

        <Grid item xs={12}>
          <CustomEditor
            txt={form.Body}
            setTxt={txt => onField('Body', txt as FormType['Body'])}
            dictonaryIdsArray={[1, 14, 2, 3]}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ mb: 1 }} />
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            mb={1}
          >
            <Typography variant='subtitle1'>Angehängte Dokumente</Typography>
            <Button
              size='small'
              startIcon={<Add />}
              onClick={() => setAddOpen(true)}
            >
              Neues Dokument
            </Button>
          </Box>
          <Box display='flex' flexWrap='wrap' gap={2}>
            {docs.length > 0 ? (
              docs.map((doc, idx) => (
                <Box key={doc.idDocument ?? idx} sx={{ position: 'relative' }}>
                  <DocumentCard initialDocument={doc} selectable={false} />
                  <IconButton
                    size='small'
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(255,255,255,0.8)',
                    }}
                    onClick={() => onRemoveDoc(idx)}
                  >
                    <Delete fontSize='small' />
                  </IconButton>
                </Box>
              ))
            ) : (
              <Typography color='textSecondary'>Keine Dokumente</Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <AddDocumentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={onAddDoc}
      />
    </>
  );
}
